import { Mail, Users, Building2, Briefcase } from "lucide-react";

import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import ContactForm from "@/components/site/ContactForm";
import { COMPANY } from "@/data/content";

const TILES = [
  {
    icon: Users,
    label: "Individual learners",
    body:
      "Aspiring train drivers, trainee drivers, qualified drivers, and individuals seeking structured development support.",
  },
  {
    icon: Building2,
    label: "Organisations & training providers",
    body:
      "Operators, training providers, heritage railways, and operational teams looking to develop drivers, instructors, managers, or safety-critical capability.",
  },
  {
    icon: Briefcase,
    label: "Business enquiries",
    body:
      "Partnerships, collaborations, supplier enquiries, and general business enquiries relating to UKTDA.",
  },
];

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact UKTDA | Railway Training & Development Enquiries"
        description="Contact UKTDA for learner, organisation, training provider, partnership, or business enquiries about railway training, competence development and professional development."
        path="/contact"
      />

      <PageHero
        eyebrow="Contact"
        title="Get in touch with UKTDA."
        intro="Tell us a little about your enquiry — we’ll route it to the right specialist area and respond as soon as practical."
      />

      <section
        className="py-20 md:py-28 bg-white"
        data-testid="contact-section"
        aria-labelledby="contact-main-heading"
      >
        <div className="container-rail grid lg:grid-cols-[2fr_3fr] gap-12 lg:gap-16 items-start">
          <div>
            <p className="label-eyebrow text-safety">How to reach us</p>

            <h2
              id="contact-main-heading"
              className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight"
            >
              Three ways we can help.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              Choose the enquiry type that best describes you. This helps us understand whether
              your message relates to individual preparation, organisational training, or a wider
              business enquiry.
            </p>

            <ul className="mt-8 space-y-4">
              {TILES.map((tile, index) => {
                const Icon = tile.icon;

                return (
                  <li
                    key={tile.label}
                    className="border border-slate-200 rounded-md p-5 flex gap-4 bg-white hover:border-safety/60 hover:shadow-sm transition-all"
                    data-testid={`contact-tile-${index}`}
                  >
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white flex-shrink-0">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>

                    <div>
                      <p className="font-display font-bold text-navy">
                        {tile.label}
                      </p>

                      <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                        {tile.body}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>

            {COMPANY.email && (
              <div className="mt-8 border-t border-slate-200 pt-6">
                <p className="label-eyebrow text-safety">Direct email</p>

                <a
                  href={`mailto:${COMPANY.email}`}
                  className="mt-3 inline-flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 bg-white text-navy hover:border-safety/60 hover:text-safety hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-safety focus-visible:ring-offset-2"
                  data-testid="contact-direct-email"
                  aria-label="Email UKTDA"
                  title="Email UKTDA"
                >
                  <Mail className="h-5 w-5" aria-hidden="true" />
                  <span className="sr-only">Email UKTDA</span>
                </a>

                <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                  You can email us directly, although the contact form is usually the best option
                  because it helps route your enquiry to the right area.
                </p>
              </div>
            )}

          </div>

          <div className="lg:sticky lg:top-8">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}